// Frame tool: drag out a frame (click for a default size). Frames go beneath
// existing content; whatever the new frame covers becomes its children when
// the gesture ends (Editor.endGesture updates membership).
import type { Frame } from '../../shared/types.ts';
import { keysBetween } from '../../shared/fractional-index.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor } from '../editor.ts';
import { rectFromPoints } from '../util/geometry.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const CLICK_PX = 4;
const DEFAULT_W = 800, DEFAULT_H = 600;

export class FrameTool implements Tool {
  cursor = 'crosshair';
  private readonly ed: Editor;
  private drag: { id: string; x: number; y: number; sx: number; sy: number } | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    const ed = this.ed;
    const count = [...ed.store.values()].filter((o) => o.type === 'frame').length;
    const obj: Frame = {
      id: newId(), type: 'frame', x: e.x, y: e.y, w: 1, h: 1, rotation: 0,
      z: keysBetween(null, ed.store.bottomZ(), 1)[0]!, createdBy: ed.userId,
      title: `Frame ${count + 1}`, children: [],
    };
    ed.beginGesture();
    ed.apply([{ t: 'create', obj }]);
    ed.renderer.setActive([obj.id]);
    this.drag = { id: obj.id, x: e.x, y: e.y, sx: e.sx, sy: e.sy };
  }

  onPointerMove(e: WorldPointerEvent): void {
    const d = this.drag;
    if (!d) return;
    const r = rectFromPoints(d.x, d.y, e.x, e.y);
    this.ed.apply([{ t: 'update', id: d.id, props: { x: r.x, y: r.y, w: Math.max(r.w, 1), h: Math.max(r.h, 1) } }]);
  }

  onPointerUp(e: WorldPointerEvent): void {
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    const ed = this.ed;
    if (Math.hypot(e.sx - d.sx, e.sy - d.sy) < CLICK_PX) {
      ed.apply([{ t: 'update', id: d.id, props: { x: d.x - DEFAULT_W / 2, y: d.y - DEFAULT_H / 2, w: DEFAULT_W, h: DEFAULT_H } }]);
    }
    ed.renderer.setActive([]);
    ed.endGesture(); // adopts the objects the frame now covers
    ed.setTool('select');
    ed.setSelection([d.id]);
  }

  onCancel(): void {
    if (!this.drag) return;
    this.drag = null;
    this.ed.renderer.setActive([]);
    this.ed.cancelGesture();
  }
}
